import { api, cachedRequest } from '../apiClient';
import { apiCache } from '../cache';
import { Cart } from '../../types';

export const cartService = {
  getCart: async (forceRefresh: boolean = false): Promise<{ cart: Cart }> => {
    return cachedRequest(
      'cart_data',
      async () => {
        const response = await api.get('/cart/');
        return response.data;
      },
      30 * 1000, // 30 seconds
      forceRefresh
    );
  },
  
  addToCart: async (itemType: string, itemId: number, quantity: number = 1, information?: Record<string, any>): Promise<{ cart: Cart; message: string }> => {
    const payload: any = {
      item_type: itemType,
      item_id: itemId,
      quantity: quantity
    };
    
    if (information) {
      payload.information = information;
    }
    
    const response = await api.post('/cart/add/', payload);
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  removeFromCart: async (itemId: number): Promise<{ cart: Cart; message: string }> => {
    const response = await api.delete(`/cart/remove/${itemId}/`);
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  clearCart: async (): Promise<{ cart: Cart; message: string }> => {
    const response = await api.post('/cart/clear/');
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  applyCoupon: async (couponCode: string): Promise<{ success: boolean; message: string; cart: Cart }> => {
    const response = await api.post('/cart/coupon/', { code: couponCode });
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  removeCoupon: async (): Promise<{ success: boolean; message: string; cart: Cart }> => {
    const response = await api.post('/cart/coupon/', { code: '' });
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  extendCartTime: async (): Promise<{ success: boolean; message: string; cart: Cart; new_expires_at?: string }> => {
    const response = await api.post('/cart/extend/');
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  getPaymentMethods: async (): Promise<{ 
    success: boolean; 
    payment_methods: Array<{ value: string; name: string; disabled: boolean }>;
    has_partial_payment_option: boolean;
    partial_payment_amount: number;
  }> => {
    return cachedRequest(
      'cart_payment_methods',
      async () => {
        const response = await api.get('/cart/payment-methods/');
        return response.data;
      },
      5 * 60 * 1000, // 5 minutes
      false
    );
  },
  
  setPaymentMethod: async (paymentChoice: string, isPartialPayment: boolean = false): Promise<{ success: boolean; message: string; cart: Cart }> => {
    const response = await api.post('/cart/payment-method/', {
      payment_choice: paymentChoice,
      is_partial_payment: isPartialPayment
    });
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  getShippingAddresses: async (): Promise<{ success: boolean; addresses: Array<any> }> => {
    return cachedRequest(
      'shipping_addresses',
      async () => {
        const response = await api.get('/cart/shipping-addresses/');
        return response.data;
      },
      5 * 60 * 1000, // 5 minutes
      false
    );
  },
  
  setShippingAddress: async (addressId: number): Promise<{ success: boolean; message: string; cart: Cart }> => {
    const response = await api.post('/cart/shipping-address/', { address_id: addressId });
    
    // Invalidate cart cache after modification
    apiCache.clearByPattern('^cart');
    
    return response.data;
  },
  
  proceedToCheckout: async (checkoutData: {
    payment_choice: string;
    is_partial_payment?: boolean;
    create_account?: boolean;
    [key: string]: any;
  }): Promise<{ success: boolean; message: string; redirect_url: string }> => {
    const response = await api.post('/cart/checkout/', checkoutData);
    
    // Invalidate cart cache after checkout
    apiCache.clearByPattern('^cart');
    
    return response.data;
  }
};

